import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Home, BookOpen, Map, Brain, ClipboardCheck, Timer, RotateCcw, Bike, Users, GraduationCap, ShieldCheck, LogIn, LogOut, FileText, Library, ChevronRight, BookMarked, Film, User, MessageSquare, Mail, Briefcase, Wand2, Target, History, Notebook, FlaskConical, Trophy, Mic, Compass, Award, Sparkles, ClipboardList, BookOpenCheck } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from './ui/sheet';
import { Button } from './ui/button';
import { useApp } from '../contexts/AppContext';
import useAudience, { AUDIENCES } from '../hooks/useAudience';

/**
 * Menu mobile complet (Sheet Shadcn) — accessible via le bouton hamburger
 * de la Topbar OU via le bouton "Plus" de la BottomNav.
 *
 * Liste TOUS les liens organisés par catégories pour ne plus rien manquer
 * sur mobile.
 *
 * Props :
 *   - trigger     : (optionnel) élément React qui déclenche l'ouverture
 *   - side        : (optionnel) côté du sheet ("right" par défaut)
 *   - open        : (optionnel) contrôle externe de l'ouverture
 *   - onOpenChange: (optionnel) callback si contrôlé en externe
 *
 * Si `open` / `onOpenChange` sont fournis, le composant fonctionne en mode
 * contrôlé (utilisé par BottomNav). Sinon, il garde son state interne et
 * affiche le `trigger` (utilisé par la Topbar mobile).
 */
export function MobileMenu({
  trigger,
  side = 'right',
  open: openProp,
  onOpenChange,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof openProp === 'boolean';
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (next) => {
    if (isControlled) {
      onOpenChange?.(next);
    } else {
      setInternalOpen(next);
    }
  };
  const { user, logout } = useApp();
  const { audience } = useAudience();
  const location = useLocation();
  const navigate = useNavigate();

  const isTeacherOrAdmin = !!(user && (user.role === 'enseignant' || user.role === 'admin'));
  const isAdmin = !!(user && user.role === 'admin');

  // ============================================================================
  // SECTIONS NAV — 3 audiences (Élève · Enseignant certifié · Futur enseignant)
  //   - Enseignant/Admin (role auth) : teacherSections (outils terrain pro)
  //   - audience='futur-enseignant'  : futurEnseignantSections (préparation CCS)
  //   - défaut (élève / visiteur)    : studentSections (apprentissage moto)
  // ============================================================================

  /* ── ENSEIGNANT (moniteur auto-école) ─── Phase 30 recentrage ────── */
  // Phase 31 fix : "Mon espace" dirige vers le BO du rôle réel (admin/moniteur),
  // pas vers /eleve/dashboard (l'admin ne doit JAMAIS atterrir sur l'espace élève).
  const backOfficeHref = isAdmin ? '/admin/dashboard' : '/moniteur/planning';
  const teacherSections = [
    {
      title: 'Outils Terrain',
      links: [
        { to: backOfficeHref, label: 'Mon espace', icon: GraduationCap },
        { to: '/enseignant/seances-live', label: 'Séances Live récentes', icon: History, badge: 'Mobile' },
        { to: '/enseignant/exercices-plateau', label: '12 exercices plateau', icon: Notebook },
      ],
    },
    {
      title: 'Préparer un cours',
      links: [
        { to: '/enseignant/creer-fiche', label: 'Wizard fiche séance', icon: Wand2 },
      ],
    },
    {
      title: 'Bibliothèque de cours',
      links: [
        { to: '/enseignant/salle', label: 'Cours en salle', icon: BookOpen },
        { to: '/enseignant/terrain', label: 'Cours plateau (REMC)', icon: Map, badge: '8 fiches' },
        { to: '/enseignant/seance-circulation', label: 'Cours circulation (REMC)', icon: Map, badge: '26 fiches' },
        { to: '/enseignant/cours-circulation', label: 'Formations 7h spécifiques', icon: Briefcase, badge: 'A2→A · 125 · BSR' },
      ],
    },
    {
      title: 'Référence pédagogique',
      links: [
        { to: '/enseignant/livret-apprentissage', label: 'Livret REMC', icon: FileText },
        { to: '/enseignant/fondements-pedagogie', label: 'Fondements pédagogie', icon: GraduationCap },
      ],
    },
    {
      title: 'Réseau & Communauté',
      links: [
        { to: '/reseau', label: 'Réseau Pro (hub)', icon: Users },
        { to: '/reseau/forum', label: 'Forum ECSR', icon: MessageSquare },
        { to: '/reseau/ressources', label: 'Ressources partagées', icon: Library },
      ],
    },
    {
      title: 'Magazine & Aide',
      links: [
        { to: '/magazine', label: 'Magazine pédagogique', icon: BookOpen },
        { to: '/enquetes', label: 'Enquêtes & Témoignages', icon: BookMarked, badge: '31' },
        { to: '/contact', label: 'Nous contacter', icon: MessageSquare },
        { to: '/newsletter', label: 'Newsletter', icon: Mail },
      ],
    },
  ];

  /* ── FUTUR ENSEIGNANT CCS ─── MASQUÉ Phase 30 (recentrage auto-école) */
  const futurEnseignantSections = [
    {
      title: 'Magazine & Inspiration',
      links: [
        { to: '/enquetes', label: 'Enquêtes ECSR', icon: BookMarked, badge: '31' },
        { to: '/magazine', label: 'Magazine pédagogique', icon: BookOpen },
      ],
    },
    {
      title: 'Changer d\u2019audience',
      links: [
        { to: '/parcours', label: 'Je suis élève', icon: User },
        { to: '/enseignant', label: 'Je suis enseignant certifié', icon: GraduationCap },
      ],
    },
    {
      title: 'Aide & Contact',
      links: [
        { to: '/contact', label: 'Nous contacter', icon: MessageSquare },
      ],
    },
  ];

  /* ── ÉLÈVE / VISITEUR (apprend à rouler) ────────────────────────── */
  const studentSections = [
    {
      title: 'Apprentissage',
      links: [
        { to: '/formation', label: 'Formation', icon: BookOpen },
        ...(user
          ? [
              { to: '/profil', label: 'Ma progression', icon: User },
              { to: '/parcours', label: 'Mon carnet', icon: Map },
            ]
          : []),
        { to: '/livret', label: 'Livret apprentissage A', icon: FileText },
      ],
    },
    {
      title: 'Code (ETM)',
      links: [
        { to: '/revision', label: 'Dossiers thématiques', icon: Brain },
        { to: '/revoir', label: 'À revoir (mes erreurs)', icon: RotateCcw },
        { to: '/programme-etm', label: 'Programme officiel ETM', icon: FileText },
      ],
    },
    {
      title: 'Pratique moto',
      links: [
        { to: '/formations-2-roues', label: 'Trames pédagogiques (P1/P2/P3 · 125 · A2→A)', icon: BookOpen, badge: 'NEW' },
        { to: '/epreuves', label: 'Plateau & Circulation', icon: ClipboardCheck },
        { to: '/videos', label: 'Tutos vidéo · Officiels + pédago', icon: Film, badge: 'MOVED' },
      ],
    },
    {
      title: 'Permis AM (BSR)',
      links: [
        { to: '/permis-am', label: 'Hub Permis AM · Dès 14 ans', icon: Bike, badge: 'NEW' },
        { to: '/permis-am/voiturette', label: 'Option Voiturette / Quadricycle léger', icon: GraduationCap, badge: 'NEW' },
        { to: '/livrets/topolino-am', label: 'Livret suivi Topolino (interactif)', icon: ClipboardList, badge: 'NEW' },
        { to: '/livrets/parcours-am', label: 'Livret pédagogique Parcours AM (36 fiches)', icon: BookOpenCheck, badge: 'NEW' },
        { to: '/permis-am/themes', label: 'Livret Thèmes du Permis AM', icon: BookMarked },
        { to: '/cours/permis-am-journee-1', label: 'Journée 1 · Tu découvres ta moto', icon: BookOpen },
        { to: '/cours/permis-am-journee-2', label: 'Journée 2 · Tu deviens conducteur', icon: GraduationCap },
      ],
    },
    {
      title: 'Formation 125 cm³',
      links: [
        { to: '/formation-125', label: 'Hub Formation 125 · 7 h · Passerelle B', icon: Bike, badge: 'NEW' },
        { to: '/formation-125/comprendre', label: 'Hub 01 · Comprendre · 2 h', icon: BookOpen },
        { to: '/formation-125/maitriser', label: 'Hub 02 · Maîtriser · 2 h plateau', icon: BookOpen },
        { to: '/formation-125/anticiper', label: 'Hub 03 · Anticiper · 3 h circulation', icon: GraduationCap },
      ],
    },
    {
      title: 'Ressources',
      links: [
        { to: '/moto-ecoles', label: 'Annuaire moto-écoles', icon: Compass, badge: 'NEW' },
        { to: '/lexique', label: 'Lexique du motard', icon: BookMarked },
        { to: '/panneaux', label: 'Galerie panneaux', icon: Library },
        { to: '/mobilites', label: 'Mobilités urbaines', icon: Bike },
        { to: '/assr', label: 'ASSR / ASR / AER', icon: GraduationCap, badge: 'Jeunes' },
      ],
    },
    {
      title: 'Magazine',
      links: [
        { to: '/magazine', label: 'Magazine pédagogique', icon: BookOpen },
        { to: '/enquetes', label: 'Enquêtes & Témoignages', icon: BookMarked, badge: '31' },
      ],
    },
    {
      title: 'Aide & Contact',
      links: [
        { to: '/contact', label: 'Nous contacter', icon: MessageSquare },
      ],
    },
  ];

  /* ── Sélection des sections selon le contexte ───────────────────── */
  let sections;
  if (isTeacherOrAdmin) {
    sections = teacherSections;
  } else if (audience === AUDIENCES.FUTUR_ENSEIGNANT) {
    sections = futurEnseignantSections;
  } else {
    sections = studentSections;
  }

  const handleNav = (to) => {
    setOpen(false);
    navigate(to);
  };

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate('/');
  };

  const isActive = (to) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {/* Le SheetTrigger n'est pas rendu en mode contrôlé : l'ouverture est
          pilotée par la prop `open` (depuis la BottomNav). */}
      {!isControlled && (
        <SheetTrigger asChild data-testid="mobile-menu-trigger">
          {trigger || (
            <Button
              variant="outline"
              size="icon"
              className="rounded-full h-9 w-9 lg:hidden"
              aria-label="Ouvrir le menu"
            >
              <Menu className="w-4 h-4" />
            </Button>
          )}
        </SheetTrigger>
      )}
      <SheetContent side={side} className="w-[88vw] sm:w-[400px] p-0 flex flex-col" data-testid="mobile-menu-sheet">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border flex-shrink-0">
          <Link to={isTeacherOrAdmin ? '/enseignant' : '/'} onClick={() => setOpen(false)} className="flex items-center min-w-0" data-testid="mobile-menu-brand" aria-label={isTeacherOrAdmin ? 'CODE 2 ROUES — Espace Pro ECSR' : 'CODE 2 ROUES — Accueil'}>
            <img
              src="/logo-c2r-compact.png"
              alt="CODE 2 ROUES — Magazine pédagogique moto 2026"
              className="h-10 w-auto object-contain"
              style={{ maxWidth: '150px' }}
            />
          </Link>
          <SheetClose asChild>
            <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 flex-shrink-0" aria-label="Fermer le menu" data-testid="mobile-menu-close">
              <X className="w-4 h-4" />
            </Button>
          </SheetClose>
        </div>

        {/* User pill */}
        {user ? (
          <div className="px-4 py-3 border-b border-border flex-shrink-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-0.5">Connecté en tant que</div>
            <div className="flex items-center justify-between gap-2">
              <div className="font-bold text-sm truncate" data-testid="mobile-menu-user-name">{user.name || user.email}</div>
              <span className={
                user.role === 'admin' ? 'mc-tag mc-tag-red' :
                  user.role === 'enseignant' ? 'mc-tag mc-tag-amber' : 'mc-tag mc-tag-blue'
              } data-testid="mobile-menu-user-role">
                {user.role === 'admin' ? 'Admin' : user.role === 'enseignant' ? 'Enseignant' : 'Élève'}
              </span>
            </div>
          </div>
        ) : (
          <div className="px-4 py-3 border-b border-border flex flex-col gap-2 flex-shrink-0">
            <Button onClick={() => handleNav('/login')} variant="outline" size="sm" className="rounded-full justify-start gap-2" data-testid="mobile-menu-login">
              <LogIn className="w-4 h-4" /> Connexion
            </Button>
            <Button onClick={() => handleNav('/register')} size="sm" className="mc-btn-primary rounded-full justify-center" data-testid="mobile-menu-register">
              Créer un compte
            </Button>
          </div>
        )}

        {/* Scrollable nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-5" data-testid="mobile-menu-nav">
          {/* ══════════════════════════════════════════════════════════════
              BLOC FEATURED — Trames pédagogiques & Livrets interactifs
              Visible en HAUT du menu mobile (90% des accès élève/enseignant)
              ══════════════════════════════════════════════════════════════ */}
          <section className="rounded-2xl border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[hsl(var(--primary))]/10 via-transparent to-transparent p-3" data-testid="mobile-menu-featured">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]">
                <BookOpenCheck className="w-3.5 h-3.5" />
              </span>
              <div className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))]">Trames pédagogiques & Livrets</div>
            </div>
            {/* Hub principal — pleine largeur */}
            <button
              onClick={() => handleNav('/formations-2-roues')}
              className="w-full flex items-center gap-2.5 rounded-xl bg-[hsl(var(--primary))] text-white px-3 py-2.5 text-sm font-black transition active:scale-95 mb-1.5"
              data-testid="mobile-menu-featured-hub"
            >
              <Sparkles className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left leading-tight">Toutes les trames 2-roues</span>
              <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-white/20 flex-shrink-0">NEW</span>
              <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 opacity-80" />
            </button>
            {/* Grille 3×2 pour trames + livrets */}
            <div className="grid grid-cols-2 gap-1.5">
              <button
                onClick={() => handleNav('/plateau-progression')}
                className="rounded-xl bg-card border border-border hover:border-[hsl(var(--primary))]/50 transition p-2 text-left active:scale-95"
                data-testid="mobile-menu-featured-plateau"
              >
                <div className="flex items-center gap-1.5 mb-0.5"><Bike className="w-3.5 h-3.5 text-rose-500" /><span className="text-[10px] font-black uppercase tracking-widest text-rose-500">Trame</span></div>
                <div className="text-[12px] font-black leading-tight">Plateau A1/A2</div>
                <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">P1 · P2 · P3</div>
              </button>
              <button
                onClick={() => handleNav('/formation-125-progression')}
                className="rounded-xl bg-card border border-border hover:border-[hsl(var(--primary))]/50 transition p-2 text-left active:scale-95"
                data-testid="mobile-menu-featured-125"
              >
                <div className="flex items-center gap-1.5 mb-0.5"><Compass className="w-3.5 h-3.5 text-cyan-600" /><span className="text-[10px] font-black uppercase tracking-widest text-cyan-600">Trame</span></div>
                <div className="text-[12px] font-black leading-tight">Formation 125</div>
                <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">7 h</div>
              </button>
              <button
                onClick={() => handleNav('/passerelle-a2-a-progression')}
                className="rounded-xl bg-card border border-border hover:border-[hsl(var(--primary))]/50 transition p-2 text-left active:scale-95"
                data-testid="mobile-menu-featured-passerelle"
              >
                <div className="flex items-center gap-1.5 mb-0.5"><Award className="w-3.5 h-3.5 text-orange-600" /><span className="text-[10px] font-black uppercase tracking-widest text-orange-600">Trame</span></div>
                <div className="text-[12px] font-black leading-tight">Passerelle A2 → A</div>
                <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">7 h</div>
              </button>
              <button
                onClick={() => handleNav('/livret-plateau')}
                className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 hover:border-emerald-500/60 transition p-2 text-left active:scale-95"
                data-testid="mobile-menu-featured-livret-plateau"
              >
                <div className="flex items-center gap-1.5 mb-0.5"><ClipboardList className="w-3.5 h-3.5 text-emerald-600" /><span className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Livret</span></div>
                <div className="text-[12px] font-black leading-tight">Plateau A1/A2</div>
                <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">Interactif · à cocher</div>
              </button>
              <button
                onClick={() => handleNav('/livret-circulation')}
                className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 hover:border-emerald-500/60 transition p-2 text-left active:scale-95"
                data-testid="mobile-menu-featured-livret-circu"
              >
                <div className="flex items-center gap-1.5 mb-0.5"><ClipboardList className="w-3.5 h-3.5 text-emerald-600" /><span className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Livret</span></div>
                <div className="text-[12px] font-black leading-tight">Circulation</div>
                <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">Interactif · à cocher</div>
              </button>
              <button
                onClick={() => handleNav('/livret-125')}
                className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 hover:border-emerald-500/60 transition p-2 text-left active:scale-95"
                data-testid="mobile-menu-featured-livret-125"
              >
                <div className="flex items-center gap-1.5 mb-0.5"><ClipboardList className="w-3.5 h-3.5 text-emerald-600" /><span className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Livret</span></div>
                <div className="text-[12px] font-black leading-tight">Formation 125</div>
                <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">Interactif · à cocher</div>
              </button>
              <button
                onClick={() => handleNav('/livret-am')}
                className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 hover:border-emerald-500/60 transition p-2 text-left active:scale-95"
                data-testid="mobile-menu-featured-livret-am"
              >
                <div className="flex items-center gap-1.5 mb-0.5"><ClipboardList className="w-3.5 h-3.5 text-emerald-600" /><span className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Livret</span></div>
                <div className="text-[12px] font-black leading-tight">Permis AM (BSR)</div>
                <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">Interactif · à cocher</div>
              </button>
              <button
                onClick={() => handleNav('/mon-livret-moto')}
                className="rounded-xl bg-card border border-border hover:border-[hsl(var(--primary))]/50 transition p-2 text-left active:scale-95"
                data-testid="mobile-menu-featured-mon-livret"
              >
                <div className="flex items-center gap-1.5 mb-0.5"><BookOpen className="w-3.5 h-3.5 text-muted-foreground" /><span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Accueil</span></div>
                <div className="text-[12px] font-black leading-tight">Mon Livret Moto</div>
                <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">Hub livrets</div>
              </button>
            </div>
          </section>

          {sections.map((sec) => (
            <div key={sec.title}>
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground px-2 mb-1.5">{sec.title}</div>
              <ul className="space-y-0.5">
                {sec.links.map((link) => {
                  const Icon = link.icon;
                  const active = isActive(link.to);
                  return (
                    <li key={link.to}>
                      <button
                        onClick={() => handleNav(link.to)}
                        className={
                          'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ' +
                          (active
                            ? 'text-white shadow-sm'
                            : 'hover:bg-[hsl(var(--panel))] text-foreground/85'
                          )
                        }
                        style={active ? { background: 'hsl(var(--primary))' } : {}}
                        data-testid={`mobile-menu-link-${link.to.replace(/\//g, '-') || 'home'}`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span className="flex-1 text-left truncate">{link.label}</span>
                        {link.badge && (
                          <span className={
                            'text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full flex-shrink-0 ' +
                            (active ? 'bg-white/20 text-white' : 'bg-[hsl(var(--primary)/_0.1)] text-[hsl(var(--primary))]')
                          }>
                            {link.badge}
                          </span>
                        )}
                        <ChevronRight className={'w-3.5 h-3.5 flex-shrink-0 ' + (active ? 'text-white/70' : 'text-muted-foreground')} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {isAdmin && (
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground px-2 mb-1.5">Administration</div>
              <button
                onClick={() => handleNav('/admin/dashboard')}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors hover:bg-[hsl(var(--panel))]"
                data-testid="mobile-menu-link-admin"
              >
                <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 text-left">Dashboard admin</span>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </div>
          )}
        </nav>

        {/* Footer logout */}
        {user && (
          <div className="border-t border-border px-3 py-3 flex-shrink-0">
            <Button
              variant="outline"
              onClick={handleLogout}
              className="w-full justify-start gap-2 rounded-xl"
              data-testid="mobile-menu-logout"
            >
              <LogOut className="w-4 h-4" /> Déconnexion
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
